import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Conversation } from '@cotera/sdk/core';
import type { ArtifactDescriptor } from './artifacts';
import { ChatArtifactModel } from './chat-artifact.model';
import {
  useWatchableValue,
  useStatefullyAwareInScopeModel,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import {
  ArtifactSidePanel,
  type ArtifactSidePanelHandle,
} from './artifact-side-panel';
import type { OpenArtifactRequest, PanelHost } from './artifact-panel-context';
import {
  artifactRegistrationFor,
  type ArtifactRegistration,
} from './artifact-registration';

const NULL_PENDING_FOCUS_WATCHABLE = Watchable.fromValue<string | null>(null);

/**
 * Whether a pending focus request can be served yet.
 *
 * Both halves are asynchronous and neither is the request's own doing. A
 * request is made the moment the `add_artifact` action returns — before the
 * conversation has reported the artifact back, so the list does not hold it
 * yet; and on a chat whose split has never opened, the panel that would take
 * the call is not mounted either.
 *
 * Served on arrival rather than on request, because the alternative is what the
 * bug was: firing once against nothing, clearing the request, and leaving the
 * artifact to turn up in the switcher a moment later with the panel still shut.
 */
export const canServeArtifactFocus = (params: {
  pendingArtifactId: string | null;
  artifacts: readonly ArtifactDescriptor[];
  /** The panel is mounted and can be told to focus and open. */
  panelReady: boolean;
}): boolean => {
  if (params.pendingArtifactId === null || !params.panelReady) {
    return false;
  }
  return params.artifacts.some(
    (artifact) => artifact.artifactId === params.pendingArtifactId
  );
};

/**
 * Mark the chat's focused (top-level) agent artifact so its `@`-mentioned
 * documents/agents get surfaced as sibling tabs. The seed sets this on the pending
 * artifact for a fresh chat, but on reload artifacts come from the conversation —
 * where a tool-message artifact can override the seeded payload and drop the flag.
 * Re-deriving it from the persisted focused agent keeps the expander on across reloads.
 * Only the focused agent is stamped, so sub-agent tabs never expand in turn.
 */
const stampExpandReferences = (
  artifacts: ArtifactDescriptor[],
  focusedAgentId: string | null
): ArtifactDescriptor[] => {
  if (focusedAgentId === null) {
    return artifacts;
  }
  return artifacts.map((artifact) => {
    if (artifact.type !== 'agent' || artifact.artifactId !== focusedAgentId) {
      return artifact;
    }
    const basePayload =
      typeof artifact.payload === 'object' && artifact.payload !== null
        ? artifact.payload
        : {};
    return { ...artifact, payload: { ...basePayload, expandReferences: true } };
  });
};

/**
 * Conversation artifacts win on id collision, but they often omit client-only
 * seed flags (`isNew`). Keep those from the seeded initial artifact so a brand-new
 * agent/workflow is not sent through ChatArtifactAccessGate before it exists.
 */
const mergeArtifactsPreferringConversation = (
  initialArtifacts: ArtifactDescriptor[],
  fromConversation: ArtifactDescriptor[]
): ArtifactDescriptor[] => {
  const byId = new Map<string, ArtifactDescriptor>();
  for (const artifact of initialArtifacts) {
    byId.set(artifact.artifactId, artifact);
  }
  for (const artifact of fromConversation) {
    const previous = byId.get(artifact.artifactId);
    const previousPayload =
      previous !== undefined &&
      typeof previous.payload === 'object' &&
      previous.payload !== null
        ? (previous.payload as Record<string, unknown>)
        : null;
    const nextPayload =
      typeof artifact.payload === 'object' && artifact.payload !== null
        ? (artifact.payload as Record<string, unknown>)
        : null;

    if (
      previousPayload?.['isNew'] === true &&
      (nextPayload === null || nextPayload['isNew'] !== true)
    ) {
      byId.set(artifact.artifactId, {
        ...artifact,
        payload: {
          ...(nextPayload ?? {}),
          isNew: true,
        },
      });
      continue;
    }

    byId.set(artifact.artifactId, artifact);
  }
  return Array.from(byId.values());
};

/**
 * Hide artifacts the user just closed until the conversation stops reporting them.
 *
 * A `remove_artifact` action only reaches this component when it broadcasts back,
 * and any unrelated conversation update in the meantime (a streaming message, say)
 * re-derives the list from a snapshot that predates the removal — which would pop
 * the closed tab back open. Each id drops out of `pendingDismissals` as soon as the
 * conversation itself excludes it, so the set stays empty in the steady state.
 */
const applyPendingDismissals = (
  artifacts: ArtifactDescriptor[],
  pendingDismissals: Set<string>
): ArtifactDescriptor[] => {
  if (pendingDismissals.size === 0) {
    return artifacts;
  }
  const present = new Set(artifacts.map((artifact) => artifact.artifactId));
  for (const artifactId of pendingDismissals) {
    if (!present.has(artifactId)) {
      pendingDismissals.delete(artifactId);
    }
  }
  return artifacts.filter(
    (artifact) => !pendingDismissals.has(artifact.artifactId)
  );
};

type ChatWithArtifactPanelProps = {
  conversation: Conversation | null;
  /**
   * The chat these artifacts belong to.
   *
   * Not read off `conversation`, which is null until the chat is saved — and a
   * seeded chat is exactly the case where the panel is holding artifacts
   * nothing else can reproduce.
   */
  conversationId: string;
  children: React.ReactNode;
  defaultArtifactPanelSize?: number;
  minArtifactPanelSize?: number;
  /** Artifacts to show when chat loads (e.g. agent when navigating from agent page). Message artifacts take precedence for same artifactId. */
  initialArtifacts?: ArtifactDescriptor[];
  /**
   * Record a workspace resource opened in the panel on the conversation (the
   * `add_artifact` chat action). Wired by the chat page; when omitted, pushes stay
   * panel-local. See {@link artifactRegistrationFor} for which pushes qualify.
   */
  onRegisterArtifact?: (registration: ArtifactRegistration) => void;
  /**
   * Retract an artifact the user closed from the conversation (the
   * `remove_artifact` chat action). Wired by the chat page; when omitted, closing
   * only drops the panel-local copy and a conversation-backed artifact comes back
   * on the next load.
   */
  onUnregisterArtifact?: (artifactId: string) => void;
  /**
   * The conversation the panel belongs to. Carries no title: the page bar one
   * row above already names the chat. What it does carry is the chat's share
   * state, mirrored into every artifact's bar so both halves of the split report
   * the same access. See {@link PanelHost}.
   */
  host?: PanelHost;
  /** Render one artifact as the whole page. See {@link ArtifactSidePanel}. */
  focusView?: {
    artifactId: string;
    onBack: () => void;
    onSelectArtifact: (artifactId: string, artifactType: string) => void;
    onShare: (artifact: ArtifactDescriptor) => void;
  };
  /** See {@link ArtifactSidePanel}. */
  onOpenFocusView?: (artifactId: string, artifactType: string) => void;
  /**
   * Whether an artifact turning up may open the panel. Default false, which is
   * what a conversation wants: it is reopened with everything it ever produced
   * still attached, so "an artifact appeared" fires on load for work finished
   * days ago, and the artifacts index card answers what is waiting at the cost
   * of a corner rather than a pane.
   *
   * A chat seeded from a resource is the exception and passes true. There the
   * chat exists *because* the reader asked to look at that one thing — opening
   * a dataset from the Library, a workflow from Automations — and the artifact
   * is the reason the page was opened rather than a leftover from earlier work.
   * See {@link ArtifactSidePanel}'s own prop.
   */
  autoOpenOnArtifact?: boolean;
};

/**
 * Conversation-backed wrapper around the generic {@link ArtifactSidePanel}. Loads
 * the conversation's artifacts (merged with `initialArtifacts`), syncs them to
 * `ChatArtifactModel`, and forwards programmatic focus requests (e.g. from the
 * command palette) to the panel.
 */
export const ChatWithArtifactPanel: React.FC<ChatWithArtifactPanelProps> = ({
  conversation,
  conversationId,
  children,
  defaultArtifactPanelSize = 50,
  // No default here: `ArtifactSidePanel` owns the floor, and re-declaring one
  // as 0 in the pass-through silently disabled it.
  minArtifactPanelSize,
  autoOpenOnArtifact = false,
  initialArtifacts = [],
  onRegisterArtifact,
  onUnregisterArtifact,
  host,
  focusView,
  onOpenFocusView,
}) => {
  const [artifacts, setArtifacts] = useState<ArtifactDescriptor[]>([]);
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;
  const panelRef = useRef<ArtifactSidePanelHandle>(null);
  // Closed here but not yet reflected by the conversation — see applyPendingDismissals.
  const pendingDismissalsRef = useRef<Set<string>>(new Set());

  const artifactPanelSyncKey = useMemo(() => {
    const initialKey =
      initialArtifacts.length === 0
        ? ''
        : initialArtifacts
            .map((artifact) => artifact.artifactId)
            .sort()
            .join('\0');
    if (conversation === null) {
      return initialKey === '' ? null : `initial:${initialKey}`;
    }
    return `${conversation.artifactPanelSyncKey()}|${initialKey}`;
  }, [conversation, initialArtifacts]);

  /**
   * Whether {@link artifacts} has been filled from the conversation yet.
   *
   * The list starts empty and arrives in two steps: the conversation loads, and
   * then its `artifacts()` promise resolves. Until both have happened, "empty"
   * means "not known yet" — which reads identically to "this chat has none", and
   * a surface that has to tell a missing artifact from a pending one cannot use
   * the length alone. The focus view is that surface: it told people their
   * artifact had been removed while the chat was still loading it.
   */
  const [artifactsResolved, setArtifactsResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const activeConversation = conversationRef.current;
    const hasInitialArtifacts = initialArtifacts.length > 0;

    if (activeConversation === null) {
      setArtifacts(hasInitialArtifacts ? initialArtifacts : []);
      // Seeded artifacts are the whole list while there is no conversation to
      // read one from; with none, this is still the loading state.
      setArtifactsResolved(hasInitialArtifacts);
      return () => {
        cancelled = true;
      };
    }

    void activeConversation.artifacts().then((fromConversation) => {
      if (cancelled) {
        return;
      }
      const focusedAgentId = activeConversation.getFocusedAgentId();
      const merged = hasInitialArtifacts
        ? mergeArtifactsPreferringConversation(
            initialArtifacts,
            fromConversation
          )
        : fromConversation;
      setArtifacts(
        applyPendingDismissals(
          stampExpandReferences(merged, focusedAgentId),
          pendingDismissalsRef.current
        )
      );
      setArtifactsResolved(true);
    });

    return () => {
      cancelled = true;
    };
  }, [artifactPanelSyncKey, initialArtifacts]);

  const chatArtifactModel = useStatefullyAwareInScopeModel(ChatArtifactModel);

  useEffect(() => {
    chatArtifactModel?.setArtifacts(artifacts);
  }, [chatArtifactModel, artifacts]);

  // Programmatic focus requests from ChatArtifactModel — a clicked resource
  // mention, the command palette. See the effect below `mergedArtifacts`, which
  // is where it is served.
  const pendingFocusArtifactId = useWatchableValue(
    chatArtifactModel?.pendingFocusArtifactIdWatchable ??
      NULL_PENDING_FOCUS_WATCHABLE
  );

  // Artifacts opened from *inside* another artifact (e.g. clicking a workflow node
  // on the canvas opens its config). Held here so they show immediately without a
  // saved chat or a server round-trip; the ones that name a workspace resource are
  // also registered on the conversation below, and the conversation-backed copy
  // takes over once it arrives. Keyed by artifactId; re-opening replaces.
  //
  // Plain component state, and it can be: focusing an artifact is a param change
  // on the chat's own route, so this component stays mounted through it. These
  // used to be parked in a module-level map keyed by conversation, because
  // focusing *was* a second route and the panel was torn down on the way in —
  // which is what dropped a tab from the switcher and what left a resource
  // opened from a focused artifact reporting it was not in this chat.
  const [transientArtifacts, setTransientArtifacts] = useState<
    ArtifactDescriptor[]
  >([]);

  // Switching chats without remounting — `/chats/a` to `/chats/b` reuses this
  // component — so the held set has to follow the id rather than the mount.
  // Adjusted during render, not in an effect: an effect would paint one frame of
  // the previous chat's tabs into this chat's switcher first.
  const heldForConversationRef = useRef(conversationId);
  if (heldForConversationRef.current !== conversationId) {
    heldForConversationRef.current = conversationId;
    setTransientArtifacts([]);
  }

  // Artifacts already registered on the conversation this mount, so re-opening a
  // tab doesn't append another `load_artifact` action for the same resource.
  const registeredArtifactIdsRef = useRef<Set<string>>(new Set());
  const artifactsRef = useRef<ArtifactDescriptor[]>(artifacts);
  artifactsRef.current = artifacts;

  const handleOpenArtifact = useCallback(
    (request: OpenArtifactRequest) => {
      const descriptor: ArtifactDescriptor = {
        type: request.type,
        artifactId: request.artifactId,
        payload: request.payload ?? null,
        messageId: '',
        toolCallId: '',
        toolName: '',
      };
      setTransientArtifacts((prev) => [
        ...prev.filter((a) => a.artifactId !== descriptor.artifactId),
        descriptor,
      ]);

      // A resource opened in the panel belongs to the chat, not just to this
      // mount: register it so it survives reload and shows up in the LLM's
      // active context. The transient copy above keeps it on screen until the
      // action round-trips back through the conversation. An unsaved chat has
      // nothing to register against, so those pushes stay panel-local.
      if (
        onRegisterArtifact === undefined ||
        conversationRef.current === null
      ) {
        return;
      }
      const registration = artifactRegistrationFor(request);
      if (
        registration === null ||
        registeredArtifactIdsRef.current.has(registration.resourceId) ||
        artifactsRef.current.some(
          (artifact) => artifact.artifactId === registration.resourceId
        )
      ) {
        return;
      }
      registeredArtifactIdsRef.current.add(registration.resourceId);
      onRegisterArtifact(registration);
    },
    [onRegisterArtifact]
  );

  const handleRemoveArtifact = useCallback(
    (artifactId: string) => {
      setTransientArtifacts((prev) =>
        prev.filter((a) => a.artifactId !== artifactId)
      );
      // Only retract what the conversation actually carries (or is about to, when
      // the registration hasn't round-tripped yet). Panel-local tabs — a workflow
      // node's config, an agent's reference tabs — were never registered, so
      // closing them must not write a dismissal to the chat.
      const isConversationBacked =
        registeredArtifactIdsRef.current.has(artifactId) ||
        artifactsRef.current.some(
          (artifact) => artifact.artifactId === artifactId
        );
      if (onUnregisterArtifact === undefined || !isConversationBacked) {
        return;
      }
      // Close the tab now; the conversation catches up when the `remove_artifact`
      // action broadcasts back.
      pendingDismissalsRef.current.add(artifactId);
      setArtifacts((prev) => prev.filter((a) => a.artifactId !== artifactId));
      // Re-opening the same resource should register it again.
      registeredArtifactIdsRef.current.delete(artifactId);
      onUnregisterArtifact(artifactId);
    },
    [onUnregisterArtifact]
  );

  // Conversation artifacts win on id collision; transient node configs never
  // collide with them in practice.
  const mergedArtifacts = useMemo(() => {
    if (transientArtifacts.length === 0) {
      return artifacts;
    }
    const persistedIds = new Set(artifacts.map((a) => a.artifactId));
    return [
      ...artifacts,
      ...transientArtifacts.filter((a) => !persistedIds.has(a.artifactId)),
    ];
  }, [artifacts, transientArtifacts]);

  /**
   * Serve a pending focus request, once there is something to focus.
   *
   * Held rather than consumed on arrival. A request is made the moment the
   * `add_artifact` action returns, which is before the conversation has
   * reported the artifact back — so at that point the list does not contain it
   * and, on a chat whose split has never opened, the panel is not even mounted
   * to take the call. Firing then cleared the request against nothing: the
   * artifact turned up in the switcher a moment later with the panel still
   * shut, which is what "it gets added but does not open" was.
   *
   * So it waits for both, and only then clears — a request means "show me this
   * when you can", and every step of getting there is asynchronous.
   */
  useEffect(() => {
    const panel = panelRef.current;
    if (
      chatArtifactModel === null ||
      pendingFocusArtifactId === null ||
      !canServeArtifactFocus({
        pendingArtifactId: pendingFocusArtifactId,
        artifacts: mergedArtifacts,
        panelReady: panel !== null,
      })
    ) {
      return;
    }
    chatArtifactModel.clearPendingFocus();
    panel?.focusArtifact(pendingFocusArtifactId);
  }, [chatArtifactModel, pendingFocusArtifactId, mergedArtifacts]);

  return (
    <ArtifactSidePanel
      ref={panelRef}
      artifacts={mergedArtifacts}
      defaultArtifactPanelSize={defaultArtifactPanelSize}
      {...(minArtifactPanelSize !== undefined && { minArtifactPanelSize })}
      artifactCardResizeHandle
      {...(host !== undefined && { host })}
      artifactsLoading={!artifactsResolved}
      {...(focusView !== undefined && { focusView })}
      {...(onOpenFocusView !== undefined && { onOpenFocusView })}
      // A chat opens as a chat. Its artifacts are whatever it has ever produced,
      // so letting their arrival open the panel hands half the window to work
      // that may be days old every time the conversation is reopened. The
      // artifacts index card says what is there and takes one click.
      //
      // Unless the chat was seeded from a resource, which is the caller's to
      // say — see the prop.
      autoOpenOnArtifact={autoOpenOnArtifact}
      onOpenArtifact={handleOpenArtifact}
      onRemoveArtifact={handleRemoveArtifact}
    >
      {children}
    </ArtifactSidePanel>
  );
};
